import { useEffect, useState } from "react";
import { useAlert } from "../context/alert-context";
import { useParams } from "react-router-dom";
import { Loader, Toggle } from "rsuite";

import { useAuth } from "../context/auth-context";
import { useConfig } from "../context/config-context";
import { useModal } from "../context/modal-context";

import FileService from "../services/fileService";
import API from "../utils/api";

import Gabarit from "../components/Gabarit";
import NotFound from "../components/NotFound";
import ContractLabel from "../components/Patient/ContractLabel";
import DeviceCard from "../components/Patient/DeviceCard";
import IncrementProtocol from "../components/Patient/IncrementProtocol";
import InfoCard from "../components/Patient/InfoCard";
import PrescriptionCard from "../components/Patient/PrescriptionCard";
import PatientTab from "../components/Patient/Tab";
import TreatmentCard from "../components/Patient/TreatmentCard";
import ModalTuto from "../components/Patient/Tuto";

export default function PatientPage() {
  const { showAlert } = useAlert();
  const [patient, setPatient] = useState(null);
  const [prescriber, setPrescriber] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSync, setIsLoadingSync] = useState(false);
  const [isLoadingIncrementationProtocol, setIsLoadingIncrementationProtocol] =
    useState(false);

  const { id } = useParams();
  let { user } = useAuth();
  const {
    showModal,
    hideModal,
    isModalVisible,
    setModalLabel,
    modalLabel,
    setModalFullScreen,
  } = useModal();

  const siteConfig = useConfig();

  useEffect(() => {
    async function defaultValue() {
      setIsLoading(true);
      const initPatient = await getPatient(id);
      const initPrescriber = await getPrescriber(initPatient.prescriberId);
      setPatient(initPatient);
      setPrescriber(initPrescriber);
      setIsLoading(false);
    }
    defaultValue();
    // eslint-disable-next-line
  }, [id]);

  const getPatient = async (patientId) => {
    try {
      const response = await API.get(`/patients/${patientId}`);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  };

  const getPrescriber = async (prescriberId) => {
    try {
      if (
        user.role === siteConfig.ROLES.PRESCRIBER &&
        user.roleInfo &&
        user.roleInfo.finess
      ) {
        return user.roleInfo;
      } else {
        const response = await API.get(`/prescribers/${prescriberId}`);
        return response.data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSync = async (patientId) => {
    try {
      setIsLoadingSync(true);
      // silent: true → pas de toast automatique sur le 202
      await API.put(`/patients/${patientId}/sync`, {}, { silent: true });

      // Poll GET /patients/:id toutes les 3s jusqu'à ce que syncInProgress soit false
      const pollInterval = setInterval(async () => {
        try {
          const response = await API.get(`/patients/${patientId}`, { silent: true });
          if (!response.data?.syncInProgress) {
            clearInterval(pollInterval);
            setPatient(response.data);
            setIsLoadingSync(false);
            showAlert("success", "Synchronisation terminée");
          }
        } catch (err) {
          clearInterval(pollInterval);
          setIsLoadingSync(false);
          showAlert("error", "Erreur lors de la synchronisation");
        }
      }, 3000);
    } catch (error) {
      console.log(error);
      setIsLoadingSync(false);
    }
  };

  const handleSign = async (prescriptionId, data, mode) => {
    try {
      const config = mode === "sign" ? {} : { responseType: "arraybuffer" };
      const response = await API.put(
        `/prescriptions/${prescriptionId}/${mode}`,
        data,
        config
      );
      return mode === "sign" ? response.data : response;
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenTutoModal = async () => {
    setModalLabel(siteConfig.components.demo_mode);
    setModalFullScreen(false);
    showModal(<ModalTuto onCloseModal={hideModal} />);
  };

  const handleSignFinish = () => {
    setIsLoading(true);
    hideModal();
    setTimeout(
      () => (window.location.href = `${siteConfig.routes.patients.path}/${id}`),
      2000
    );
  };

  const handleDownloadIncrementationProtocol = async () => {
    try {
      setIsLoadingIncrementationProtocol(true);
      const fileService = new FileService();
      if (!process.env.REACT_APP_DEMO) {
        await fileService.downloadIncrementationProtocol(patient._id);
      } else {
        fileService.downloadProtocolIncrementationDemoFile();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingIncrementationProtocol(false);
    }
  };

  if (isLoading) {
    return (
      <Gabarit className="flex items-center justify-center">
        <Loader className="p-4" />
      </Gabarit>
    );
  } else if (!patient) {
    return <NotFound />;
  }

  return (
    <Gabarit>
      <div className="flex flex-col gap-3 p-5 bg-white rounded-lg shadow-xl">
        <div className="flex flex-row justify-between">
          <Toggle
            checked={
              isModalVisible && modalLabel === siteConfig.components.demo_mode
            }
            onChange={handleOpenTutoModal}
            checkedChildren={siteConfig.components.demo_mode}
            unCheckedChildren={siteConfig.components.demo_mode}
          />
          <div className="flex gap-2">
            {siteConfig.features.incrementProtocol &&
              patient.protocolIncrementation && (
                <IncrementProtocol
                  isLoading={isLoadingIncrementationProtocol}
                  handleDownload={handleDownloadIncrementationProtocol}
                />
              )}
            <ContractLabel type={patient?.contract?.type} />
          </div>
        </div>
        <div className="flex flex-col justify-between gap-3 lg:flex-row">
          <InfoCard
            patient={patient}
            isAdmin={user.role === siteConfig.ROLES.ADMIN}
            handleSync={handleSync}
            isLoadingSync={isLoadingSync}
          />
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:min-w-[820px]">
            <div className="flex flex-col w-full gap-3">
              <TreatmentCard traitement={patient.traitement} patient={patient} />
              <DeviceCard
                patient={patient}
                pump={patient.pump}
                devices={patient.devices || []}
                lastIntervention={patient.lastIntervention}
              />
            </div>
            <div className="flex flex-col w-full gap-3">
              <PrescriptionCard
                prescriptions={patient.currentPrescriptions}
                viewStatusSano={siteConfig.pages.patient.authorization.viewStatusSano.includes(
                  user.role
                )}
              />
              {siteConfig.features.renewal.display && (
                <PrescriptionCard
                  renewalMode
                  prescriptions={patient.renewablePrescriptions}
                  handleSign={handleSign}
                  hasSignAccess={
                    siteConfig.features.renewal.active &&
                    (siteConfig.pages.patient.authorization.hasSignAccess.includes(
                      user.role
                    ) ||
                      (user.role === siteConfig.ROLES.ADMIN &&
                        user.isSuperAdmin))
                  }
                  printMode={
                    siteConfig.features.renewal.active &&
                    (siteConfig.pages.patient.authorization.printMode.includes(
                      user.role
                    ) ||
                      (user.role === siteConfig.ROLES.ADMIN &&
                        !user.isSuperAdmin))
                  }
                  viewStatusSano={siteConfig.pages.patient.authorization.viewStatusSano.includes(
                    user.role
                  )}
                  isSuperAdmin={
                    user.role === siteConfig.ROLES.ADMIN && user.isSuperAdmin
                  }
                  prescriber={prescriber}
                  prescribers={patient.prescribers}
                  handleSignFinish={handleSignFinish}
                />
              )}
            </div>
          </div>
        </div>
        <PatientTab
          className="mt-5"
          patient={patient}
          metrics={patient.metrics}
        />
      </div>
    </Gabarit>
  );
}
